import * as React from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { postDeleteUser } from 'src/api/oApi';
import { generalStore } from 'src/stores/GeneralStore';
import { authStore } from 'src/stores/AuthStore';
import { UserRoles } from 'src/models/enum';

/**
 * Compliance-only "Delete User" action for the backoffice. Mirrors the app's self-service deletion:
 * it goes through the same guarded server flow (POST /support/deleteUser -> performAccountDeletion),
 * so it refuses while the user still holds assets/balance, closes the Mangopay account, and records
 * the deletion in the Deletions collection (which stays visible in the deletions view). Rendered only
 * for complianceUser; support users don't see it.
 */
export const DeleteUserButtonComponent: React.FC<{ userId: string; forceReload?: () => void }> = observer(
  ({ userId, forceReload }) => {
    const [open, setOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    // Compliance-only action.
    if (authStore.role !== UserRoles.ComplianceUser) return null;

    const submit = async () => {
      setIsLoading(true);
      try {
        const res = await postDeleteUser({ userId });
        const state = res?.data?.data?.state;
        generalStore.setMessage(
          state === 'running'
            ? 'Deletion started — user has transactions, so the record is retained (deactivated) for statutory retention.'
            : 'User deleted.'
        );
        setOpen(false);
        if (forceReload) forceReload();
      } catch (error: any) {
        const message = error?.response?.data?.error?.message;
        if (message === 'USER_HAS_ASSETS_OR_BALANCE') {
          generalStore.setError('Cannot delete: the user still holds assets or a balance. Resolve those first.');
        } else {
          generalStore.setError(message || error?.message || 'Error deleting user', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <>
        <Button variant="contained" color="error" onClick={() => setOpen(true)}>
          Delete User
        </Button>
        <Dialog open={open} onClose={() => (isLoading ? null : setOpen(false))} maxWidth="sm" fullWidth>
          <DialogTitle>Delete user</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This permanently deletes the account (GDPR right-to-erasure). It is refused while the user still holds assets or a
              balance. A user with past transactions is retained (deactivated, state &quot;running&quot;) for the statutory
              retention period; otherwise the record is fully removed. The Mangopay account is closed. This cannot be undone.
            </Alert>
            <DialogContentText>
              Delete user <strong>{userId}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <LoadingButton loading={isLoading} color="error" variant="contained" onClick={submit}>
              Delete User
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);
